import * as React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { withTranslation } from 'react-i18next';

import { SearchInput } from '../../shared/ListFilters';
import { format, parseISO } from '../hooks/dateUtils';

const MIN_QUERY_LENGTH = 2;

/**
 * Mobile equivalent of the desktop calendar search: find a work order by registration
 * number, work number or customer and jump the day view to it.
 */
function MobileCalendarSearchDialog({ t, open, onClose, onSelect }) {
  const [queryDraft, setQueryDraft] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [searched, setSearched] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setQueryDraft('');
      setResults([]);
      setError(null);
      setSearched(false);
    }
  }, [open]);

  const runSearch = async (rawTerm) => {
    const term = (rawTerm || '').trim();
    if (term.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/scheduling/calendar/search/', { params: { q: term } });
      setResults(response.data?.results || []);
    } catch (err) {
      console.error('Calendar search failed:', err);
      setError(t('Search failed'));
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const describe = (result) => {
    if (!result.is_scheduled || !result.start_time) {
      return t('Not scheduled yet');
    }
    const start = parseISO(result.start_time);
    return `${format(start, 'dd.MM.yyyy')} ${format(start, 'HH:mm')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen data-testid="mobile-calendar-search-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        {t('Search work orders')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SearchInput
          value={queryDraft}
          onChange={(value) => {
            setQueryDraft(value);
            // Phones have no comfortable Enter; the input debounces for us.
            runSearch(value);
          }}
          onEnter={runSearch}
          onClear={() => {
            setQueryDraft('');
            setResults([]);
            setSearched(false);
          }}
          placeholder={t('Registration or work number')}
          maxWidth="100%"
          autoFocus
          data-testid="mobile-calendar-search-input"
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Typography variant="body2" color="error.main" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && searched && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('No matches')}
          </Typography>
        )}

        <List>
          {results.map((result) => (
            <ListItemButton
              key={`${result.schedule_id || 'unscheduled'}-${result.maintenance_id}`}
              onClick={() => onSelect(result)}
              data-testid={`mobile-calendar-search-result-${result.maintenance_id}`}
            >
              <ListItemText
                primary={[result.vehicle_registration, result.work_number ? `#${result.work_number}` : null]
                  .filter(Boolean)
                  .join('  ')}
                secondary={[describe(result), result.customer_name].filter(Boolean).join(' · ')}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(MobileCalendarSearchDialog);
